/**
 * A grade horária — a peça que define o produto.
 *
 * O sistema de design inteiro foi desenhado para esta tela: os tokens
 * `--grid-*` estavam parados em `tokens.css` desde a fundação, esperando
 * exatamente isto. As regras que ela obedece são as do DESIGN.md, e valem
 * repetir porque é aqui que elas mordem:
 *
 * **Régua, não cartão.** A célula não tem borda própria nem sombra: o que
 * separa uma aula da outra é o fio da grade. Uma grade de cartões com sombra
 * vira um tabuleiro de botões e a semana some.
 *
 * **A cor pertence ao dado.** Cada disciplina recebe um dos dez matizes da
 * paleta categórica, e mais nada na tela usa cor — nem cabeçalho, nem
 * moldura, nem o seletor de eixo. Numa grade de 30 células, cada cor gasta no
 * cromo é uma disciplina a menos que se distingue.
 *
 * **O intervalo aparece.** Ele não é aula e mesmo assim ocupa uma faixa, mais
 * fina e sem cor. Escondê-lo faria o buraco entre a terceira e a quarta aula
 * parecer erro do sistema.
 *
 * **Três ângulos do mesmo fato.** Por turma é o que o aluno vê; por professor
 * é o que ele leva no bolso; por local diz se a quadra bate. A troca de
 * ângulo não vai ao servidor: a grade vem uma vez, inteira.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import {
  BASE_EVENTOS,
  ErroApi,
  type Achado,
  horario as api,
  type AlocacaoGrade,
  type Cenario,
  type Enfase,
  type Eixos,
  type Horario,
  type RelaxacaoGrade,
  type SlotGrade,
} from '../api'
import { Icon } from '../components/Icon'
import { rotulosDistintos } from '../rotulos'
import { Regras } from './Regras'
import { Diferencas } from './Diferencas'
import { Mover } from './Mover'
import { Rodada } from './Rodada'

type Eixo = 'turma' | 'professor' | 'local'

const DIAS = ['', 'Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta', 'Sábado', 'Domingo']
const DIAS_CURTOS = ['', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb', 'Dom']

export function Grade({ projeto }: { projeto: string }) {
  const [cenarios, setCenarios] = useState<Cenario[]>([])
  const [cenarioId, setCenarioId] = useState<string | null>(null)
  const [gradeLida, setGrade] = useState<Horario | null>(null)
  const [eixos, setEixos] = useState<Eixos | null>(null)
  const [eixo, setEixo] = useState<Eixo>('turma')
  const [foco, setFoco] = useState<string | null>(null)
  const [erro, setErro] = useState<string | null>(null)
  const [carregando, setCarregando] = useState(true)
  const [processando, setProcessando] = useState(false)
  /** O que o worker está fazendo agora, vindo do fluxo de eventos. */
  const [statusVivo, setStatusVivo] = useState<string | null>(null)
  /** Desde quando. Alimenta o cronômetro — ver `Andamento`. */
  const [desde, setDesde] = useState<number | null>(null)
  const [enfases, setEnfases] = useState<Enfase[]>([])
  /** O painel "Rodar de novo" aberto. */
  const [rodando, setRodando] = useState(false)
  /** A aula aberta para mover. */
  const [movendo, setMovendo] = useState<AlocacaoGrade | null>(null)

  useEffect(() => {
    Promise.all([api.cenarios(projeto), api.eixos(projeto), api.enfases(projeto)])
      .then(([cs, es, en]) => {
        setCenarios(cs)
        setEixos(es)
        setEnfases(en)
        setCenarioId((atual) => atual ?? cs[0]?.id ?? null)
        setErro(null)
      })
      .catch((e) => setErro(e instanceof ErroApi ? e.message : 'não foi possível carregar'))
      .finally(() => setCarregando(false))
  }, [projeto])

  const recarregar = useCallback(async () => {
    if (!cenarioId) return
    try {
      setGrade(await api.ler(projeto, cenarioId))
      setErro(null)
    } catch (e) {
      setErro(e instanceof ErroApi ? e.message : 'não foi possível ler a grade')
    }
  }, [projeto, cenarioId])

  useEffect(() => {
    void recarregar()
  }, [recarregar])

  /**
   * A grade aparece sozinha quando o worker termina.
   *
   * Um solve leva minutos, e sem isto a pessoa ou fica olhando a tela ou vai
   * fazer outra coisa e volta sem saber. O botão "Atualizar" continua ali de
   * propósito: nenhuma conexão viva sobrevive a um notebook fechado, e uma
   * tela sem saída manual quando o aviso não chega é pior do que uma sem aviso
   * nenhum.
   *
   * `EventSource` reconecta sozinho por especificação — não há laço de
   * reconexão aqui porque o navegador já tem um.
   */
  const emProgresso = useRef<string | null>(null)
  useEffect(() => {
    const fonte = new EventSource(`${BASE_EVENTOS}/projetos/${projeto}/eventos`, {
      withCredentials: true,
    })

    fonte.addEventListener('processamento', (e) => {
      const aviso = JSON.parse((e as MessageEvent).data) as {
        cenario_id: string
        status: string
      }
      if (aviso.cenario_id !== cenarioId) return

      if (aviso.status === 'CONCLUIDO' || aviso.status === 'FALHOU') {
        emProgresso.current = null
        setDesde(null)
        void recarregar()
        void api.cenarios(projeto).then(setCenarios)
      } else {
        emProgresso.current = aviso.status
        setDesde((atual) => atual ?? Date.now())
      }
      setStatusVivo(emProgresso.current)
    })

    return () => fonte.close()
  }, [projeto, cenarioId, recarregar])

  async function processar() {
    if (!cenarioId) return
    setProcessando(true)
    setErro(null)
    try {
      await api.processar(projeto, cenarioId)
    } catch (e) {
      setErro(
        e instanceof ErroApi
          ? e.message
          : 'não foi possível enfileirar o processamento',
      )
    } finally {
      setProcessando(false)
    }
  }

  // Os nomes na célula também passam pela regra do homônimo (P1): na
  // vitrine, uma aula com as duas Sorayas aparecia "SORAYA LIMA COSTA, SORAYA
  // LIMA COSTA" — e não dava para saber se era a mesma, duas vezes.
  const grade = useMemo(() => {
    if (!gradeLida || !eixos) return gradeLida
    const rotulos = rotulosDistintos(eixos.professores)
    return {
      ...gradeLida,
      alocacoes: gradeLida.alocacoes.map((a) => ({
        ...a,
        professores: a.professores_ids.map((id, i) => rotulos.get(id) ?? a.professores[i] ?? id),
      })),
    }
  }, [gradeLida, eixos])

  /** Quem pode ocupar o eixo escolhido. */
  const opcoes = useMemo(() => {
    if (!eixos) return []
    if (eixo === 'turma') return eixos.turmas.map((t) => ({ id: t.id, nome: t.nome }))
    if (eixo === 'professor') {
      const rotulos = rotulosDistintos(eixos.professores)
      return eixos.professores.map((p) => ({ id: p.id, nome: rotulos.get(p.id) ?? p.nome }))
    }
    return eixos.locais.map((l) => ({ id: l.id, nome: l.nome }))
  }, [eixos, eixo])

  useEffect(() => {
    setFoco((atual) => (opcoes.some((o) => o.id === atual) ? atual : (opcoes[0]?.id ?? null)))
  }, [opcoes])

  /**
   * A cor de cada disciplina.
   *
   * Pela posição na lista ordenada **por nome**, e não por id: assim a
   * atribuição é estável entre uma abertura e outra (a professora não quer
   * que Matemática mude de cor) e previsível para quem olha — Arte é sempre a
   * primeira. A ordem da paleta foi montada para separar ao máximo os matizes
   * vizinhos, e é essa ordem que se gasta aqui.
   *
   * **A paleta tem dez cores e uma grade brasileira tem doze disciplinas.**
   * Duas repetem, e isso é limite conhecido, não descuido: esticar a paleta
   * para catorze diluiria a separação de matiz que a faz funcionar. O que
   * segura o caso é que o nome da disciplina está em negrito na própria
   * célula — a cor é pista de varredura, não identificador. Quando a escola
   * se importa com um par específico, ela define `disciplina.cor` e essa
   * vence.
   */
  const corDa = useMemo(() => {
    const porNome = new Map<string, string>()
    for (const a of grade?.alocacoes ?? []) {
      if (a.disciplina_id && a.disciplina) porNome.set(a.disciplina, a.disciplina_id)
    }
    const mapa = new Map<string, number>()
    ;[...porNome.keys()]
      .sort((x, y) => x.localeCompare(y, 'pt-BR'))
      .forEach((nome, i) => mapa.set(porNome.get(nome)!, (i % 10) + 1))
    return (id: string | null) => (id ? (mapa.get(id) ?? 1) : 1)
  }, [grade])

  if (carregando) return <p className="field__hint">Carregando…</p>

  if (cenarios.length === 0) {
    return (
      <>
        <header className="page-head">
          <h1>Grade</h1>
        </header>
        <div className="empty">
          <p className="empty__title">Nenhum cenário ainda.</p>
          <p className="empty__body">
            Um cenário é uma versão da grade. Vale ter mais de um para comparar — o mesmo
            conjunto de dados montado com pesos diferentes produz horários diferentes, e a
            comparação lado a lado é o que mostra qual vale a pena.
          </p>
        </div>
      </>
    )
  }

  return (
    <>
      <header className="page-head">
        <h1>Grade</h1>
        <p className="page-head__note">
          {statusVivo ? <Andamento status={statusVivo} desde={desde} /> : grade ? resumo(grade) : '—'}
        </p>
      </header>

      {erro && (
        <p className="state state--pendencia">
          <Icon name="aviso" size={14} />
          {erro}
        </p>
      )}

      <Controles
        cenarios={cenarios}
        cenarioId={cenarioId}
        onCenario={setCenarioId}
        eixo={eixo}
        onEixo={setEixo}
        opcoes={opcoes}
        foco={foco}
        onFoco={setFoco}
        processando={processando || statusVivo !== null}
        onProcessar={processar}
        onRecarregar={recarregar}
        podeRodar={!!grade && grade.alocacoes.some((a) => a.tipo === 'AULA')}
        onRodar={() => setRodando(true)}
        baixar={
          cenarioId && grade?.alocacoes.some((a) => a.tipo === 'AULA')
            ? `${BASE_EVENTOS}/projetos/${projeto}/cenarios/${cenarioId}/exportar/grade-oficial.xlsx`
            : null
        }
        baixarProfessor={
          eixo === 'professor' && foco && cenarioId && grade?.alocacoes.some((a) => a.tipo === 'AULA')
            ? `${BASE_EVENTOS}/projetos/${projeto}/cenarios/${cenarioId}/exportar/professor/${foco}`
            : null
        }
      />

      {rodando && grade && cenarioId && (
        <Rodada
          projeto={projeto}
          base={{ id: cenarioId, nome: cenarios.find((c) => c.id === cenarioId)?.nome ?? '' }}
          grade={grade}
          eixos={eixos}
          aoFechar={() => setRodando(false)}
          aoCriar={async (novo) => {
            setCenarios(await api.cenarios(projeto))
            setCenarioId(novo)
            setRodando(false)
          }}
        />
      )}

      {cenarioId && (
        <Origem projeto={projeto} cenario={cenarios.find((c) => c.id === cenarioId)} cenarios={cenarios} />
      )}

      {cenarioId && enfases.length > 0 && (
        <Enfases
          enfases={enfases}
          atual={cenarios.find((c) => c.id === cenarioId)?.enfase ?? 'EQUILIBRADO'}
          aoEscolher={async (escolhida) => {
            await api.definirEnfase(projeto, cenarioId, escolhida)
            setCenarios(await api.cenarios(projeto))
          }}
        />
      )}

      {cenarioId && <Analise projeto={projeto} cenario={cenarioId} />}

      {cenarioId && <Regras projeto={projeto} cenario={cenarioId} versao={grade} />}

      {grade && foco && (
        <Semana
          grade={grade}
          eixo={eixo}
          foco={foco}
          corDa={corDa}
          onAbrir={statusVivo ? undefined : (a) => a.tipo === 'AULA' && setMovendo(a)}
        />
      )}

      {movendo && cenarioId && (
        <Mover
          projeto={projeto}
          cenario={cenarioId}
          aula={movendo}
          aoMudar={() => void recarregar()}
          aoFechar={() => setMovendo(null)}
        />
      )}

      {grade && grade.alocacoes.length === 0 && (
        <div className="empty no-print">
          <p className="empty__title">Este cenário ainda não foi processado.</p>
          <p className="empty__body">
            Processar enfileira o trabalho; o motor resolve à parte e a grade aparece aqui. Uma
            escola grande leva cerca de um minuto.
          </p>
          <div className="empty__actions">
            <button type="button" className="btn btn--primary" onClick={processar}>
              <Icon name="processar" size={14} />
              Processar grade
            </button>
          </div>
        </div>
      )}

      {grade && grade.pendencias.length > 0 && <Pendencias grade={grade} />}
    </>
  )
}

/**
 * De onde este cenário veio, quando é uma rodada. É o que substitui o nome à
 * mão das planilhas dela — `25 6 16h45 EXPERIMENTAL` — por uma frase que diz o
 * que foi congelado e a partir de quê.
 */
function Origem({
  projeto,
  cenario,
  cenarios,
}: {
  projeto: string
  cenario: Cenario | undefined
  cenarios: Cenario[]
}) {
  if (!cenario?.base_id) return null
  const base = cenarios.find((c) => c.id === cenario.base_id)
  return (
    <details className="grade__origem no-print">
      <summary className="field__hint">
        A partir de <strong>{base?.nome ?? 'um cenário apagado'}</strong>
        {cenario.descricao ? ` — ${cenario.descricao}.` : '.'} Ver o que mudou.
      </summary>
      {/* A comparação é com a base COMO ELA ESTÁ AGORA. Se a base foi mexida
          depois de esta rodada nascer, a diferença inclui o que mudou nela —
          visto na vitrine: uma disciplina congelada aparecia como "movida"
          porque a base tinha sido reprocessada. Dito, em vez de enganar. */}
      <p className="field__hint">
        Comparado com {base?.nome ?? 'a base'} como está agora. Se {base?.nome ?? 'ela'} foi mexido
        depois, a diferença inclui também o que mudou lá.
      </p>
      {base && <Diferencas projeto={projeto} cenario={cenario.id} />}
    </details>
  )
}

function resumo(grade: Horario): string {
  const aulas = grade.alocacoes.filter((a) => a.tipo === 'AULA').length
  const faltando = grade.pendencias.reduce((s, p) => s + p.aulas_nao_alocadas, 0)
  if (aulas === 0) return 'sem grade ainda'
  return faltando === 0
    ? `${aulas} aulas · grade completa`
    : `${aulas} aulas · ${faltando} sem lugar`
}

// ---------------------------------------------------------------------------

function Controles({
  cenarios,
  cenarioId,
  onCenario,
  eixo,
  onEixo,
  opcoes,
  foco,
  onFoco,
  processando,
  onProcessar,
  onRecarregar,
  podeRodar,
  onRodar,
  baixar,
  baixarProfessor,
}: {
  cenarios: Cenario[]
  cenarioId: string | null
  onCenario: (id: string) => void
  eixo: Eixo
  onEixo: (e: Eixo) => void
  opcoes: { id: string; nome: string }[]
  foco: string | null
  onFoco: (id: string) => void
  processando: boolean
  onProcessar: () => void
  onRecarregar: () => void
  podeRodar: boolean
  onRodar: () => void
  /** Endereço da planilha da grade; nulo enquanto não há grade. */
  baixar: string | null
  /** A grade do professor em foco, para o e-mail individual. */
  baixarProfessor: string | null
}) {
  return (
    <div className="grade__controles no-print">
      <div className="field">
        <label className="field__label" htmlFor="grade-cenario">
          Cenário
        </label>
        <select
          className="select"
          id="grade-cenario"
          value={cenarioId ?? ''}
          onChange={(e) => onCenario(e.target.value)}
        >
          {cenarios.map((c) => (
            <option key={c.id} value={c.id}>
              {c.nome}
              {c.pendencias > 0 ? ` — ${c.pendencias} sem lugar` : ''}
            </option>
          ))}
        </select>
      </div>

      <div className="field">
        <span className="field__label">Ver por</span>
        <div className="segmented segmented--texto" role="group" aria-label="Eixo da grade">
          {(['turma', 'professor', 'local'] as const).map((valor) => (
            <button
              key={valor}
              type="button"
              className="segmented__option"
              aria-pressed={eixo === valor}
              onClick={() => onEixo(valor)}
            >
              {valor === 'turma' ? 'Turma' : valor === 'professor' ? 'Professor' : 'Local'}
            </button>
          ))}
        </div>
      </div>

      <div className="field grade__foco">
        <label className="field__label" htmlFor="grade-foco">
          {eixo === 'turma' ? 'Turma' : eixo === 'professor' ? 'Professor' : 'Local'}
        </label>
        <select
          className="select"
          id="grade-foco"
          value={foco ?? ''}
          onChange={(e) => onFoco(e.target.value)}
        >
          {opcoes.map((o) => (
            <option key={o.id} value={o.id}>
              {o.nome}
            </option>
          ))}
        </select>
      </div>

      <div className="grade__acoes">
        <button type="button" className="btn btn--secondary" onClick={onRecarregar}>
          Atualizar
        </button>
        <button
          type="button"
          className="btn btn--secondary"
          onClick={onRodar}
          disabled={!podeRodar || processando}
          title={
            podeRodar
              ? 'Congelar parte desta grade e refazer o resto'
              : 'Processe este cenário antes: rodar de novo parte de uma grade pronta'
          }
          data-tour="rodar"
        >
          Rodar de novo…
        </button>
        {/* Link e não botão: é um arquivo, e a mesma origem leva o cookie de
            sessão sem uma linha de código. */}
        {baixar && (
          <a className="btn btn--secondary" href={baixar} download data-tour="baixar">
            Baixar planilha
          </a>
        )}
        {baixarProfessor && (
          <a className="btn btn--secondary" href={baixarProfessor} download>
            Baixar a grade deste professor
          </a>
        )}
        <button
          type="button"
          className="btn btn--primary"
          onClick={onProcessar}
          disabled={processando}
        >
          <Icon name="processar" size={14} />
          {processando ? 'Enfileirando…' : 'Processar'}
        </button>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------

function Semana({
  grade,
  eixo,
  foco,
  corDa,
  onAbrir,
}: {
  grade: Horario
  eixo: Eixo
  foco: string
  corDa: (id: string | null) => number
  /** Abrir a aula para mover. Ausente = grade só de leitura. */
  onAbrir?: (a: AlocacaoGrade) => void
}) {
  // As alocações que interessam ao foco escolhido.
  const minhas = grade.alocacoes.filter((a) =>
    eixo === 'turma'
      ? a.turma_id === foco
      : eixo === 'professor'
        ? a.professores_ids.includes(foco)
        : a.local_id === foco,
  )

  const violadas = new Set(grade.violacoes.flatMap((v) => v.alocacoes_ids))

  // As linhas da grade são os INSTANTES, não os slots: dois esquemas podem
  // descrever o mesmo horário com ids diferentes, e uma linha por slot faria
  // a semana aparecer duplicada para quem atende dois turnos.
  const linhas = useMemo(() => {
    const porInstante = new Map<string, SlotGrade[]>()
    for (const s of grade.slots) {
      const chave = `${s.inicio}|${s.duracao_min}|${s.tipo}`
      porInstante.set(chave, [...(porInstante.get(chave) ?? []), s])
    }
    return [...porInstante.entries()]
      .map(([chave, slots]) => ({ chave, slots, modelo: slots[0]! }))
      .sort((a, b) => a.modelo.inicio.localeCompare(b.modelo.inicio))
  }, [grade.slots])

  const dias = useMemo(
    () => [...new Set(grade.slots.map((s) => s.dia))].sort((a, b) => a - b),
    [grade.slots],
  )

  const porSlot = new Map<string, AlocacaoGrade[]>()
  for (const a of minhas) {
    porSlot.set(a.slot_id, [...(porSlot.get(a.slot_id) ?? []), a])
  }

  return (
    <div className="grade__rolagem">
      <table className="grade" style={{ ['--grade-dias' as string]: dias.length }}>
        <thead>
          <tr>
            <th className="grade__gutter" scope="col">
              <span className="sr-only">Horário</span>
            </th>
            {dias.map((dia) => (
              <th key={dia} scope="col">
                <span className="grade__dia-longo">{DIAS[dia]}</span>
                <span className="grade__dia-curto" aria-hidden>
                  {DIAS_CURTOS[dia]}
                </span>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {linhas.map(({ chave, slots, modelo }) => {
            const ehAula = modelo.tipo === 'AULA'
            return (
              <tr key={chave} className={ehAula ? undefined : 'grade__linha--intervalo'}>
                <th className="grade__gutter mono" scope="row">
                  {modelo.inicio}
                  {!ehAula && <span className="grade__rotulo-pausa">recreio</span>}
                </th>
                {dias.map((dia) => {
                  const doDia = slots.filter((s) => s.dia === dia)
                  const aulas = doDia.flatMap((s) => porSlot.get(s.id) ?? [])
                  return (
                    <td key={dia}>
                      {ehAula &&
                        aulas.map((a) => (
                          <Celula
                            key={a.id}
                            alocacao={a}
                            eixo={eixo}
                            cat={corDa(a.disciplina_id)}
                            violada={violadas.has(a.id)}
                            onAbrir={onAbrir}
                          />
                        ))}
                    </td>
                  )
                })}
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

/**
 * Uma aula na grade.
 *
 * O que aparece muda com o eixo, e isso não é enfeite: na grade da turma a
 * pergunta é "que matéria é agora e com quem"; na do professor, "que turma me
 * espera". Mostrar tudo sempre entulha a célula, que tem 7rem de largura.
 */
function Celula({
  alocacao,
  eixo,
  cat,
  violada,
  onAbrir,
}: {
  alocacao: AlocacaoGrade
  eixo: Eixo
  cat: number
  violada: boolean
  onAbrir?: (a: AlocacaoGrade) => void
}) {
  if (alocacao.tipo === 'HORA_ATIVIDADE') {
    return (
      <div className="grade__aula grade__aula--atividade" title="Hora-atividade">
        <span className="grade__principal">Hora-atividade</span>
        {alocacao.professores[0] && eixo !== 'professor' && (
          <span className="grade__secundario">{alocacao.professores[0]}</span>
        )}
      </div>
    )
  }

  const principal =
    eixo === 'turma' ? alocacao.disciplina : eixo === 'professor' ? alocacao.turma : alocacao.turma
  const secundario =
    eixo === 'turma'
      ? alocacao.professores.join(', ')
      : eixo === 'professor'
        ? alocacao.disciplina
        : `${alocacao.disciplina ?? ''} · ${alocacao.professores.join(', ')}`

  // A cor que a escola definiu vence a da paleta. Ela entra só no fio da
  // esquerda, e não na lavagem do fundo: uma cor escolhida sem pensar em
  // contraste — o azul-marinho que alguém achou bonito — tornaria o texto
  // ilegível. O fio carrega a identidade sem pôr o texto em risco.
  const estilo = {
    '--cat-wash': `var(--cat-${cat}-wash)`,
    '--cat-ink': `var(--cat-${cat}-ink)`,
    '--cat-rule': alocacao.disciplina_cor || `var(--cat-${cat}-rule)`,
  } as React.CSSProperties

  const conteudo = (
    <>
      <span className="grade__principal">{principal ?? '—'}</span>
      {secundario && <span className="grade__secundario">{secundario}</span>}

      {/* Forma além da cor, sempre: fixada é hachura e cadeado, violação é
          triângulo, cesta é a palavra. Numa grade impressa em preto e branco
          os três continuam legíveis. */}
      <span className="grade__marcas">
        {alocacao.na_cesta && (
          <span className="grade__marca grade__marca--cesta" title="Na cesta: posta fora da disponibilidade, em negociação">
            cesta
          </span>
        )}
        {alocacao.fixada && (
          <span className="grade__marca" title="Fixada por você">
            <Icon name="fixada" size={11} />
          </span>
        )}
        {violada && (
          <span className="grade__marca grade__marca--aviso" title="Tem violação">
            <Icon name="aviso" size={11} />
          </span>
        )}
      </span>
    </>
  )
  const titulo = `${alocacao.disciplina ?? ''} · ${alocacao.turma ?? ''} · ${alocacao.professores.join(', ')}`

  // Botão e não div com onClick: mover uma aula tem de ser alcançável por
  // teclado, e um div clicável não é.
  if (onAbrir) {
    return (
      <button
        type="button"
        className="grade__aula grade__aula--botao"
        style={estilo}
        title={`${titulo} — clique para mover ou travar`}
        onClick={() => onAbrir(alocacao)}
      >
        {conteudo}
      </button>
    )
  }
  return (
    <div className="grade__aula" style={estilo} title={titulo}>
      {conteudo}
    </div>
  )
}

/**
 * O que não coube.
 *
 * Fica ao pé da grade e não numa aba: uma pendência escondida atrás de um
 * clique é uma turma sem professor que ninguém viu. E cada linha diz turma e
 * disciplina, porque "3 pendências" não é acionável e "faltam 2 aulas de
 * Matemática na 1ª A" é.
 *
 * **O veredito vem antes da lista.** Foi o primeiro desenho ao contrário, e
 * bastou ver com dado real: com 21 linhas de pendência, a única frase que
 * muda o que a pessoa vai fazer — "é regra" ou "foi tempo" — ficava abaixo da
 * dobra. A lista é a evidência; a resposta vem primeiro.
 */
function Pendencias({ grade }: { grade: Horario }) {
  const total = grade.pendencias.reduce((s, p) => s + p.aulas_nao_alocadas, 0)
  const frases = frasesDas(grade.diagnostico?.relaxacoes ?? [])
  return (
    <section className="section">
      <div className="section__head">
        <h2>Aulas sem lugar</h2>
        <p>
          {total} aula{total === 1 ? '' : 's'} em {grade.pendencias.length} disciplina
          {grade.pendencias.length === 1 ? '' : 's'}
        </p>
      </div>

      <Diagnostico grade={grade} />

      <h3 className="sub-head">As aulas que ficaram de fora</h3>
      <ul className="ruled">
        {grade.pendencias.map((p) => (
          <li key={p.oferta_id}>
            <span className="ruled__key">{p.turma}</span>
            <span className="ruled__val">
              <span className="state state--pendencia">
                <Icon name="pendencia" size={13} />
                {p.aulas_nao_alocadas}
              </span>{' '}
              {p.disciplina}
            </span>
            <span className="ruled__note">
              {p.restricoes_ids.length > 0
                ? p.restricoes_ids.map((id) => frases.get(id) ?? id).join(' · ')
                : p.motivo}
            </span>
          </li>
        ))}
      </ul>
    </section>
  )
}

/** O rótulo interno de uma regra traduzido pela frase que o motor já escreveu. */
function frasesDas(relaxacoes: RelaxacaoGrade[]): Map<string, string> {
  return new Map(
    relaxacoes.flatMap((r) => r.restricoes_resolvidas.map((id) => [id, r.alvo_descricao])),
  )
}

/**
 * Por que não fechou.
 *
 * **A primeira linha é a única que não pode faltar.** "É regra" e "foi tempo"
 * levam a ações opostas — mexer numa restrição, ou simplesmente rodar de novo
 * — e uma tela que trata as duas como "deu ruim" faz o coordenador desmontar
 * uma regra sadia e voltar com a mesma grade. Por isso o veredito vem antes
 * das sugestões, e por isso `INDETERMINADO` diz "não consegui provar" em vez
 * de escolher um dos dois.
 *
 * **As sugestões vêm em dois grupos, e a separação é a honestidade da tela.**
 * O núcleo que o solver devolve não é garantidamente mínimo: ele traz regras
 * que estão no conflito mas que, afrouxadas sozinhas, não resolvem nada. O
 * motor verifica cada uma; aqui as provadas ficam em cima, sob a promessa de
 * que qualquer uma delas basta, e as outras ficam embaixo sem promessa
 * nenhuma. Misturar as duas seria prometer o que não se cumpre.
 *
 * O custo não aparece como número. Ele é uma ordem de incômodo, não uma
 * medida — exibi-lo como "50" faria a tela afirmar uma precisão que o motor
 * não tem. A ordem da lista já é o que ele significa.
 */
function Diagnostico({ grade }: { grade: Horario }) {
  const d = grade.diagnostico
  if (!d || d.conclusao === 'NAO_EXECUTADO') {
    return (
      <p className="field__hint">
        O diagnóstico não rodou nesta execução. Ele é o passe que diz se o que falta é regra
        ou é tempo.
      </p>
    )
  }

  if (d.conclusao === 'TEMPO') {
    return (
      <p className="veredito veredito--tempo">
        <Icon name="processar" size={14} />
        <span>
          <strong>Não é regra sua: foi o tempo.</strong> As regras de hoje permitem uma grade
          completa — o motor só não a encontrou dentro do orçamento. Processar de novo tende a
          fechar. Não há nada para afrouxar.
        </span>
      </p>
    )
  }

  if (d.conclusao === 'INDETERMINADO') {
    return (
      <p className="veredito veredito--indeterminado">
        <Icon name="aviso" size={14} />
        <span>
          <strong>O motor não conseguiu provar de quem é a culpa.</strong> O orçamento do
          diagnóstico acabou antes. Não dá para dizer se é regra ou se é tempo — e chutar aqui
          mandaria mexer numa regra que talvez não tenha nada de errado.
        </span>
      </p>
    )
  }

  const bastam = d.relaxacoes.filter((r) => r.resolve_sozinha)
  const envolvidas = d.relaxacoes.filter((r) => !r.resolve_sozinha)

  return (
    <>
      <p className="veredito veredito--restricao">
        <Icon name="aviso" size={14} />
        <span>
          <strong>Não é falta de tempo.</strong> Com as regras de hoje não existe grade completa
          — processar de novo devolve o mesmo. Alguma coisa precisa ceder.
        </span>
      </p>

      {bastam.length > 0 && (
        <>
          <h3 className="sub-head">Qualquer uma destas resolve</h3>
          <ul className="ruled">
            {bastam.map((r) => (
              <Sugestao key={r.id} r={r} />
            ))}
          </ul>
        </>
      )}

      {envolvidas.length > 0 && (
        <>
          <h3 className="sub-head">Envolvidas no conflito, mas nenhuma sozinha basta</h3>
          <ul className="ruled ruled--fraca">
            {envolvidas.map((r) => (
              <Sugestao key={r.id} r={r} />
            ))}
          </ul>
        </>
      )}
    </>
  )
}

function Sugestao({ r }: { r: RelaxacaoGrade }) {
  return (
    <li>
      <span className="ruled__val">
        {r.alvo_descricao}
        {r.valor_atual && (
          <span className="troca">
            <span className="troca__de">{r.valor_atual}</span>
            <span aria-hidden="true">→</span>
            <span className="troca__para">{r.valor_proposto}</span>
          </span>
        )}
      </span>
    </li>
  )
}


/**
 * O que não vai fechar, antes de gastar cinco minutos descobrindo.
 *
 * Fica ACIMA da grade e não ao pé dela: o lugar dela é antes de a pessoa
 * apertar "Processar", porque a única coisa que ela economiza é o
 * processamento. Ao pé, chegaria tarde por construção.
 *
 * Silêncio é o estado normal e não merece caixa nenhuma. Uma tela que anuncia
 * "nenhum problema encontrado" toda vez treina a pessoa a não ler o espaço
 * onde o problema apareceria.
 */
function Analise({ projeto, cenario }: { projeto: string; cenario: string }) {
  const [achados, setAchados] = useState<Achado[] | null>(null)

  useEffect(() => {
    let vivo = true
    api
      .analise(projeto, cenario)
      .then((r) => vivo && setAchados(r.achados))
      .catch(() => vivo && setAchados(null))
    return () => {
      vivo = false
    }
  }, [projeto, cenario])

  if (!achados?.length) return null

  const impedem = achados.filter((a) => a.gravidade === 'IMPEDE')

  return (
    <section className="section no-print">
      <div className="section__head">
        <h2>Antes de processar</h2>
        <p>
          {impedem.length > 0
            ? `${impedem.length} ${impedem.length === 1 ? 'coisa impede' : 'coisas impedem'} a grade de fechar`
            : `${achados.length} ponto(s) de atenção`}
        </p>
      </div>

      <ul className="ruled">
        {achados.map((a, i) => (
          <li key={`${a.tipo}-${i}`}>
            <span
              className={
                a.gravidade === 'IMPEDE'
                  ? 'state state--pendencia'
                  : 'state state--violacao'
              }
            >
              <Icon name="aviso" size={13} />
              {a.gravidade === 'IMPEDE' ? 'impede' : 'atenção'}
            </span>
            <span className="ruled__val">{a.mensagem}</span>
          </li>
        ))}
      </ul>

      {impedem.length > 0 && (
        <p className="field__hint">
          Isto é aritmética, não previsão: as contas acima não fecham, e nenhum
          tempo de processamento muda isso. Dá para processar mesmo assim — o que
          não couber sai como aula pendente, e cada conta destas vira uma linha
          em Pendências, para alguém cuidar.
        </p>
      )}
    </section>
  )
}


/**
 * O motor está trabalhando, e há quanto tempo.
 *
 * **Barra indeterminada, e o cronômetro do lado.** A frase sozinha não
 * distinguia "está pensando" de "travou" — foi assim que ela foi lida na
 * primeira vez que alguém esperou de verdade. Movimento resolve isso: o que
 * se mexe está vivo.
 *
 * **Não há porcentagem, e não vai haver inventada.** O CP-SAT não sabe dizer
 * quanto falta: ele tem uma grade e continua procurando uma melhor até o
 * orçamento acabar. Uma barra que enche em passo constante seria uma
 * animação bonita afirmando um progresso que ninguém mediu — e pior que a
 * ausência dela, porque quando chegasse a 99%% e não terminasse, a pessoa
 * deixaria de acreditar em qualquer outra barra do produto.
 *
 * O que dá para dizer com honestidade é o tempo decorrido e o teto. Com isso
 * a pergunta "travou?" se responde sozinha.
 */
function Andamento({ status, desde }: { status: string; desde: number | null }) {
  const [agora, setAgora] = useState(Date.now())

  useEffect(() => {
    const id = setInterval(() => setAgora(Date.now()), 1000)
    return () => clearInterval(id)
  }, [])

  const s = desde ? Math.max(0, Math.floor((agora - desde) / 1000)) : 0
  const decorrido = `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`

  return (
    <span className="andamento">
      <span className="andamento__barra" aria-hidden />
      <span>
        {status === 'EXECUTANDO' ? 'o motor está montando a grade' : 'na fila'}
        {desde && <span className="mono andamento__tempo"> {decorrido}</span>}
      </span>
      <span className="sr-only" role="status">
        {status === 'EXECUTANDO' ? 'Processando a grade.' : 'Na fila.'}
      </span>
    </span>
  )
}


/**
 * A pergunta que precede os 32 pesos.
 *
 * O produto tem uma tela que expõe os critérios um a um, e **ninguém entra
 * nela**. Isso não é suposição: a coordenadora que usa o URÂNIA há anos,
 * perguntada sobre a tela equivalente lá, respondeu *"não faço a menor ideia,
 * é novo"*. O que ela usa é esta escolha — uma frase, não trinta e dois
 * números.
 *
 * **Fica antes do botão Processar, e não numa tela de configuração.** É aqui
 * que a decisão é tomada: no instante em que se vai montar a grade, não numa
 * sessão de ajustes que acontece uma vez por ano e é esquecida.
 *
 * Régua, não cartão, como o resto. O selecionado recebe o acento; os outros
 * ficam legíveis, porque trocar de ênfase é a operação frequente — a pessoa
 * roda, compara, troca, roda de novo.
 */
function Enfases({
  enfases,
  atual,
  aoEscolher,
}: {
  enfases: Enfase[]
  atual: Enfase['id']
  aoEscolher: (e: Enfase['id']) => Promise<void>
}) {
  const [ocupado, setOcupado] = useState<string | null>(null)

  return (
    <section className="section no-print">
      <div className="section__head">
        <h2>Ao montar, dar preferência a</h2>
        <p>muda o peso dos 32 critérios de uma vez</p>
      </div>

      <ul className="ruled enfases">
        {enfases.map((e) => (
          <li key={e.id}>
            <button
              type="button"
              className="enfases__opcao"
              aria-pressed={e.id === atual}
              disabled={ocupado !== null}
              onClick={async () => {
                if (e.id === atual) return
                setOcupado(e.id)
                try {
                  await aoEscolher(e.id)
                } finally {
                  setOcupado(null)
                }
              }}
            >
              <span className="enfases__marca" aria-hidden>
                {e.id === atual ? <Icon name="ok" size={13} /> : null}
              </span>
              <span>
                <strong>{e.nome}</strong>
                <span className="enfases__descricao">{e.descricao}</span>
              </span>
            </button>
          </li>
        ))}
      </ul>

      <p className="field__hint">
        A escolha vale na próxima vez que você processar. Os valores de cada
        critério são nossos, não medidos do URÂNIA — lá o rádio ficou numa
        opção só durante toda a demonstração, e não houve como ver o que cada
        uma escreve.
      </p>
    </section>
  )
}
